
import { useState, useCallback } from 'react';

/**
 * Fallback copy using execCommand for when Clipboard API is blocked
 * (e.g. after async work, or when not in a secure context / user gesture).
 */
function fallbackCopyText(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    document.body.removeChild(textarea);
    return false;
  }
}

/**
 * Clipboard utility hook for copying text.
 * Tries navigator.clipboard first; on NotAllowedError (e.g. after async delay)
 * falls back to document.execCommand('copy').
 */
export function useClipboard(resetDelay: number = 2000) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copy = useCallback(async (text: string) => {
    try {
      try {
        await navigator.clipboard.writeText(text);
      } catch (clipboardErr) {
        // NotAllowedError often happens when copy runs after async (user gesture lost)
        const fallbackOk = fallbackCopyText(text);
        if (fallbackOk) {
          setCopied(true);
          setError(null);
          setTimeout(() => setCopied(false), resetDelay);
          return true;
        }
        throw clipboardErr;
      }
      setCopied(true);
      setError(null);
      setTimeout(() => setCopied(false), resetDelay);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy');
      setCopied(false);
      return false;
    }
  }, [resetDelay]);

  /** Synchronous copy using execCommand. Use when copy must run in same user gesture (e.g. after no await). */
  const copySync = useCallback((text: string): boolean => {
    const ok = fallbackCopyText(text);
    if (ok) {
      setCopied(true);
      setError(null);
      setTimeout(() => setCopied(false), resetDelay);
    } else {
      setError('Failed to copy');
      setCopied(false);
    }
    return ok;
  }, [resetDelay]);

  const reset = useCallback(() => {
    setCopied(false);
    setError(null);
  }, []);

  return { copy, copySync, copied, error, reset };
}

export default useClipboard;
